import { ApolloError, useApolloClient } from '@apollo/client'
import { VerificaConexaoServidorDocument } from 'graphql/hooks.generated'
import { VerificaConexaoServidorQuery } from 'graphql/types.generated'
import { useLogger } from 'hooks/useLogger'
import { useEffect, useState } from 'react'
import { timeout } from 'util/promise'

export const PROBE_INTERVAL_MS = 4_000
export const PROBE_TIMEOUT_MS = 3_000
export const PROBE_FAILURES_TO_OFFLINE = 2

interface UseConnectionHeartbeatOptions {
  enabled: boolean
}

export function useConnectionHeartbeat({ enabled }: UseConnectionHeartbeatOptions) {
  const logger = useLogger()
  const client = useApolloClient()

  const [isServerReachable, setIsServerReachable] = useState(true)

  useEffect(() => {
    if (!enabled) {
      setIsServerReachable(true)
      return
    }

    let isStopped = false
    let failures = 0
    let currentAbort: AbortController | undefined

    const probe = async (): Promise<boolean> => {
      const abortController = new AbortController()
      currentAbort = abortController
      try {
        const { data } = await timeout(
          client.query<VerificaConexaoServidorQuery>({
            query: VerificaConexaoServidorDocument,
            // 'no-cache' e obrigatorio: 'network-only' grava ROOT_QUERY.serverTime na cache a cada probe
            // e desregula o getServerTimeNow do ServerTimeProvider, que observa o mesmo campo
            fetchPolicy: 'no-cache',
            context: { queryDeduplication: false, fetchOptions: { signal: abortController.signal } },
          }),
          PROBE_TIMEOUT_MS
        )

        return !!data?.serverTime
      } catch (error) {
        abortController.abort()
        // Erro de GraphQL sem networkError significa que o servidor respondeu, logo está alcançável.
        // Sem isso, qualquer erro de aplicação (ex.: 'Acesso não permitido') derruba o participante
        // por falsa perda de conexão, mesmo com a rede intacta.
        return isServerAnsweredError(error)
      }
    }

    const tick = async () => {
      const isOnline = await probe()

      if (isStopped) return

      if (isOnline) {
        logger.info('Ping bem sucedido, conexão estável')

        failures = 0
        setIsServerReachable(true)
      } else {
        failures += 1

        logger.info(`Ping mal sucedido, tentativas: ${failures}`)

        if (failures >= PROBE_FAILURES_TO_OFFLINE) {
          setIsServerReachable(false)
          logger.info('Ping mal sucedido, iniciar tentativa de reconexão...')
        }
      }
    }

    logger.info('Iniciando verificação de conexão com o servidor...')
    tick()

    const interval = setInterval(tick, PROBE_INTERVAL_MS)

    return () => {
      isStopped = true
      currentAbort?.abort()
      clearInterval(interval)
    }
  }, [enabled, logger, client])

  return { isServerReachable }
}

const isServerAnsweredError = (error: unknown): boolean => {
  const { graphQLErrors, networkError } = (error ?? {}) as ApolloError
  return !networkError && graphQLErrors?.length > 0
}
